import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Facebook, MessageCircle, Link2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/common";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [status, setStatus] = useState("checking");
  const [category, setCategory] = useState(null);
  const sid = params.get("session_id");
  useEffect(() => {
    if (!sid) { setStatus("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.category) setCategory(data.category);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (data.category === "support") { try { localStorage.setItem("mtaa_supported_at", String(Date.now())); } catch { /* ignore */ } }
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setStatus("error"); return; }
      } catch { /* transient while payment settles; keep polling */ }
      if (++tries < 8) setTimeout(poll, 1500); else setStatus("pending");
    };
    poll();
  }, [sid]);
  const copy = () => { navigator.clipboard.writeText(window.location.origin); toast.success("Link copied"); };
  const isSupport = category === "support";
  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-4 py-16 text-center">
      {status === "checking" && <><Spinner className="h-8 w-8" /><p>Confirming your payment…</p></>}
      {status === "paid" && (
        <>
          <CheckCircle2 className="h-16 w-16 text-primary" />
          {isSupport ? (
            <>
              <h1 className="font-display text-3xl font-extrabold" data-testid="support-thanks">Thank you for supporting Mtaa ❤️</h1>
              <p className="text-muted-foreground">Your support helps us keep building for the community.</p>
              <Link to="/app"><Button data-testid="pay-success-home">Back to Mtaa</Button></Link>
            </>
          ) : (
            <>
              <h1 className="font-display text-3xl font-extrabold">You're featured 🎉</h1>
              <p className="text-muted-foreground">Your promotion is now active. Share it to reach more people.</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => window.open("https://www.facebook.com/sharer/sharer.php?u=" + encodeURIComponent(window.location.origin), "_blank")}><Facebook className="h-4 w-4" />Facebook</Button>
                <Button variant="outline" onClick={() => window.open("https://wa.me/?text=" + encodeURIComponent(window.location.origin), "_blank")}><MessageCircle className="h-4 w-4" />WhatsApp</Button>
                <Button variant="outline" onClick={copy}><Link2 className="h-4 w-4" />Copy</Button>
              </div>
              <Link to="/app"><Button data-testid="pay-success-home">Back to Mtaa</Button></Link>
            </>
          )}
        </>
      )}
      {status === "pending" && <><Spinner className="h-8 w-8" /><p>Payment is processing. It may take a moment — check your profile shortly.</p><Link to="/app"><Button variant="outline">Back to Mtaa</Button></Link></>}
      {status === "error" && <><XCircle className="h-16 w-16 text-destructive" /><h1 className="font-display text-2xl font-bold">Payment not completed</h1><Link to="/app"><Button>Back to Mtaa</Button></Link></>}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-4 py-16 text-center">
      <XCircle className="h-16 w-16 text-muted-foreground" />
      <h1 className="font-display text-2xl font-bold">Payment cancelled</h1>
      <p className="text-muted-foreground">No charge was made. You can try again anytime.</p>
      <Link to="/app"><Button>Back to Mtaa</Button></Link>
    </div>
  );
}
